import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * ユーザーディレクトリ slice。
 *
 * 背景:
 * - メッセージ / DM / ショットコメントの既読・ダウンロード既読アイコンは `user.avatar`
 *   を個々のメッセージに含まれる `receivers` / `downloaded_users` から参照している。
 * - WebSocket の `message.*.read` / `*.download_read` で新たにユーザーが既読化したとき、
 *   その人物が過去にそのスレッドで登場していないと avatar URL が未知のため
 *   アイコンが壊れた状態で表示される（リロードで REST 取り直しになるまで直らない）。
 *
 * 解決策:
 * - REST レスポンス（メッセージ一覧 / メンバー一覧など）で受け取ったユーザー情報を
 *   本 slice に集約 upsert する。
 * - `<UserAvatar userId={...} fallbackAvatar={...} />` が本 slice を参照し、
 *   メッセージ内の avatar が欠けていても Redux から解決する。
 *
 * 更新戦略:
 * - 非 null の avatar / 非空の display_name を優先し、欠けている情報で既存データを
 *   上書きしない。これにより WS 経由のプレースホルダ挿入（avatar=null）が
 *   有効な avatar を潰さない。
 */
export interface UserDirectoryEntry {
  id: number;
  display_name: string;
  avatar: string | null;
}

/** upsert 時の入力型（既存値を潰したくないので optional）。 */
export interface UserDirectoryUpsert {
  id: number;
  display_name?: string | null;
  avatar?: string | null;
}

interface UserDirectoryState {
  byId: Record<number, UserDirectoryEntry>;
}

const initialState: UserDirectoryState = {
  byId: {},
};

const userDirectorySlice = createSlice({
  name: "userDirectory",
  initialState,
  reducers: {
    /**
     * 複数ユーザーをまとめて upsert する。
     * 既存エントリがある場合、`display_name` / `avatar` のいずれも
     * **非 null / 非空の入力のみ** を反映する（欠け情報で上書きしない）。
     *
     * 実質変更が無い場合はエントリそのものを上書きしない（useSelector が
     * 参照等価で差分検知するため、不要な再レンダーを避ける）。
     */
    upsertUsers(state, action: PayloadAction<UserDirectoryUpsert[]>) {
      action.payload.forEach((u) => {
        if (!u || typeof u.id !== "number") return;
        const existing = state.byId[u.id];
        const nextName =
          typeof u.display_name === "string" && u.display_name
            ? u.display_name
            : existing?.display_name ?? "";
        const nextAvatar =
          typeof u.avatar === "string" && u.avatar
            ? u.avatar
            : existing?.avatar ?? null;
        if (
          existing &&
          existing.display_name === nextName &&
          existing.avatar === nextAvatar
        ) {
          return;
        }
        state.byId[u.id] = {
          id: u.id,
          display_name: nextName,
          avatar: nextAvatar,
        };
      });
    },
    /** ログアウト時などにディレクトリをクリアする。 */
    resetUserDirectory(state) {
      state.byId = {};
    },
  },
});

export const { upsertUsers, resetUserDirectory } = userDirectorySlice.actions;

export default userDirectorySlice.reducer;

// ─── Selectors ──────────────────────────────────────

export interface UserDirectoryRootState {
  userDirectory: UserDirectoryState;
}

export const selectUserDirectoryEntry =
  (userId: number | string | undefined | null) =>
  (state: UserDirectoryRootState): UserDirectoryEntry | undefined => {
    if (userId === undefined || userId === null) return undefined;
    const numericId = typeof userId === "number" ? userId : Number(userId);
    if (Number.isNaN(numericId)) return undefined;
    return state.userDirectory.byId[numericId];
  };
